import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import SearchInput from "../../../components/search-input/search-input";
import {
  useGetInvoiceGroupsQuery,
  useGetInvoiceGroupDetailsQuery,
  useGetCompaniesQuery,
  useCreateInvoiceGroupMutation,
  useGetInvoiceQuery,
  useGetInvoiceLineItemQuery,
} from "../endpoints/billing.endpoints";
import StyledDropdown from 'src/components/styled-dropdown';
import InvoiceTableRefactored from '../components/invoice-table-refactored';
import { Button } from 'src/components/ui/button';
import { Plus } from 'lucide-react';
import { canSyncInvoice } from '../utils/billing-utils';
import { ReduxState } from 'src/redux/redux-state';
import CreateBillingCycleDrawer from '../components/create-billing-cycle-drawer';
import { useBillingGenerationToast } from '../hooks/use-billing-generation-toast';
import { useBillingJobProgress, isBillingJobInProgress } from '../hooks/use-billing-job-progress';
import { useBillingInvoiceWebSocket, BillingInvoiceWsMessage } from '../hooks/use-billing-invoice-websocket';
import { showNotification, removeNotification } from 'src/redux/notification/notification.slice';
import { InvoiceGroupDetails } from 'src/redux/billing/billing.types';
import type { BillingJobStatusValue } from 'src/redux/billing/billing.types';
import { applyInvoiceListPatch, normaliseInvoiceGroupDetails } from '../utils/invoice-list-helpers';

function getLastMonthAndYear(): { month: number; year: number } {
  const now = new Date();
  const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return { month: lastMonth, year };
}

const InvoicesTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceIdParam = searchParams.get('invoiceId');
  const lineItemIdParam = searchParams.get('lineItemId');

  const { data: lineForDeepLink } = useGetInvoiceLineItemQuery(lineItemIdParam ?? '', {
    skip: !lineItemIdParam,
  });

  const effectiveInvoiceId =
    invoiceIdParam?.trim() ||
    (lineForDeepLink?.invoice && String(lineForDeepLink.invoice).trim() ? String(lineForDeepLink.invoice) : null);

  const { data: invoiceForDeepLink } = useGetInvoiceQuery(effectiveInvoiceId ?? '', {
    skip: !effectiveInvoiceId,
  });

  const [searchValue, setSearchValue] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const defaultMonthYear = useMemo(getLastMonthAndYear, []);
  const [createCycleDrawerOpen, setCreateCycleDrawerOpen] = useState(false);
  const [createCycleMonth, setCreateCycleMonth] = useState(defaultMonthYear.month);
  const [createCycleYear, setCreateCycleYear] = useState(defaultMonthYear.year);

  // API queries
  const { data: invoiceGroupsData, refetch: refetchInvoiceGroups } = useGetInvoiceGroupsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: invoiceGroupDetails, isLoading: isLoadingInvoiceGroupDetails, isFetching: isFetchingInvoiceGroupDetails, refetch: refetchInvoiceGroupDetails } = useGetInvoiceGroupDetailsQuery(
    selectedGroupId,
    {
      skip: !selectedGroupId,
      refetchOnMountOrArgChange: true,
    }
  );

  const [liveInvoiceGroupDetails, setLiveInvoiceGroupDetails] = useState<InvoiceGroupDetails | undefined>();

  useEffect(() => {
    setLiveInvoiceGroupDetails((prev) => (prev?.id === selectedGroupId ? prev : undefined));
  }, [selectedGroupId]);

  const { isInProgress: isBillingGenerationInProgress, status: billingJobStatus } =
    useBillingJobProgress(selectedGroupId);
  const previousBillingJobStatusRef = useRef<BillingJobStatusValue | undefined>();

  useEffect(() => {
    const previousStatus = previousBillingJobStatusRef.current;
    if (
      isBillingJobInProgress(previousStatus)
      && billingJobStatus === 'complete'
    ) {
      refetchInvoiceGroupDetails();
      setIsGeneratingInvoices(false);
    }
    previousBillingJobStatusRef.current = billingJobStatus;
  }, [billingJobStatus, refetchInvoiceGroupDetails]);

  useEffect(() => {
    if (invoiceGroupDetails?.id === selectedGroupId) {
      setLiveInvoiceGroupDetails(normaliseInvoiceGroupDetails(invoiceGroupDetails));
    }
  }, [invoiceGroupDetails, selectedGroupId]);

  const displayedInvoiceGroupDetails = useMemo(() => {
    if (!selectedGroupId) {
      return undefined;
    }
    if (liveInvoiceGroupDetails?.id === selectedGroupId) {
      return liveInvoiceGroupDetails;
    }
    if (invoiceGroupDetails?.id === selectedGroupId) {
      return invoiceGroupDetails;
    }
    return undefined;
  }, [selectedGroupId, liveInvoiceGroupDetails, invoiceGroupDetails]);

  const isLoadingSelectedGroup =
    Boolean(selectedGroupId)
    && (isLoadingInvoiceGroupDetails || isFetchingInvoiceGroupDetails)
    && displayedInvoiceGroupDetails?.id !== selectedGroupId;

  const handleInvoiceWebSocketEvent = useCallback((message: BillingInvoiceWsMessage) => {
    if (message.type !== 'invoice_update' || !message.invoice_id || !message.event) {
      return;
    }
    setLiveInvoiceGroupDetails((prev) => {
      if (!prev) {
        return prev;
      }
      return applyInvoiceListPatch(
        prev,
        message.invoice_id as string,
        message.invoice ?? null,
        message.event as 'created' | 'updated' | 'deleted',
      );
    });
  }, []);

  const { liveUpdatesAvailable } = useBillingInvoiceWebSocket({
    invoiceGroupId: selectedGroupId,
    enabled: Boolean(selectedGroupId),
    onInvoiceEvent: handleInvoiceWebSocketEvent,
    onReconnect: () => {
      refetchInvoiceGroupDetails();
    },
  });

  const { data: companies } = useGetCompaniesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [createInvoiceGroup, { isLoading: isCreatingInvoiceGroup }] = useCreateInvoiceGroupMutation();
  const dispatch = useDispatch();
  const { startTracking: startBillingGenerationToast } = useBillingGenerationToast();
  const userState = useSelector((state: ReduxState) => state.userState);
  const isBillingAdmin = canSyncInvoice(userState.permissions?.groups ?? []);

  // Sort invoice groups by most recent first
  const sortedInvoiceGroups = (invoiceGroupsData || [])
    .slice()
    .sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });

  useEffect(() => {
    if (invoiceForDeepLink?.invoice_group) {
      setSelectedGroupId(invoiceForDeepLink.invoice_group);
    }
  }, [invoiceForDeepLink]);

  useEffect(() => {
    if (lineItemIdParam && lineForDeepLink?.invoice_group && !effectiveInvoiceId) {
      setSelectedGroupId(lineForDeepLink.invoice_group);
    }
  }, [lineItemIdParam, lineForDeepLink, effectiveInvoiceId]);

  const consumeInvoiceDeepLink = useCallback(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('invoiceId');
        p.delete('lineItemId');
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Auto-select the most recent invoice group by default when data loads
  useEffect(() => {
    if (invoiceIdParam || lineItemIdParam) {
      return;
    }
    const sorted = (invoiceGroupsData || [])
      .slice()
      .sort((a, b) => (a.year !== b.year ? b.year - a.year : b.month - a.month));
    if (sorted.length > 0 && !selectedGroupId) {
      setSelectedGroupId(sorted[0].id);
    }
  }, [invoiceGroupsData, selectedGroupId, invoiceIdParam, lineItemIdParam]);

  const formatMonthYear = (month: number, year: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  // Dropdown options: existing invoice groups only (no create option)
  const getDropdownOptions = () => {
    return sortedInvoiceGroups.map(group => ({
      value: group.id,
      label: formatMonthYear(group.month, group.year),
      subtitle: `${group.month}/${group.year}`,
    }));
  };

  const handleDropdownSelect = (value: string) => {
    setSelectedGroupId(value);
  };

  const openCreateCycleDrawer = () => {
    const { month, year } = getLastMonthAndYear();
    setCreateCycleMonth(month);
    setCreateCycleYear(year);
    setCreateCycleDrawerOpen(true);
  };

  const handleCreateBillingCycle = async(month: number, year: number) => {
    dispatch(
      showNotification({
        id: 'billing-generation-starting',
        title: 'Billing generation',
        summary: 'Starting billing generation…',
        theme: 'neutral',
        sticky: true,
      })
    );

    try {
      setIsGeneratingInvoices(true);
      const newInvoiceGroup = await createInvoiceGroup({
        month,
        year,
        isInitial: true,
      }).unwrap();

      dispatch(
        removeNotification({
          id: 'billing-generation-starting',
          summary: '',
          theme: 'neutral',
        })
      );
      startBillingGenerationToast(newInvoiceGroup.id);
      setSelectedGroupId(newInvoiceGroup.id);
      setCreateCycleDrawerOpen(false);
      await refetchInvoiceGroups();
      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } catch (error) {
      console.error('Failed to create invoice group:', error);
      setIsGeneratingInvoices(false);
      dispatch(
        removeNotification({
          id: 'billing-generation-starting',
          summary: '',
          theme: 'neutral',
        })
      );
      dispatch(
        showNotification({
          title: 'Billing generation',
          summary: 'Failed to start billing generation.',
          theme: 'warning',
          sticky: true,
        })
      );
    }
  };


  const handleGenerateInvoices = async(groupId: string) => {
    const invoiceGroup = sortedInvoiceGroups.find(group => group.id === groupId);
    if (!invoiceGroup) return;

    setIsGeneratingInvoices(true);
    startBillingGenerationToast(groupId);

    try {
      await createInvoiceGroup({
        month: invoiceGroup.month,
        year: invoiceGroup.year,
        isInitial: false,
      }).unwrap();
    } catch (error) {
      console.error('Failed to generate invoices:', error);
      setIsGeneratingInvoices(false);
    }
  };

  return (
    <div className="h-full flex flex-col space-y-4">

      <div className="">
        <div className="flex flex-row gap-4 w-full items-center justify-between pb-1">
          <div>
            <SearchInput
              placeholder="Search invoices..."
              value={searchValue}
              onChange={setSearchValue}
              debounceMs={500}
              className="w-96 rounded-full"
              inputClassName='bg-white rounded-full '
            />
          </div>
          <div className="flex items-center gap-2">
            <StyledDropdown
              options={getDropdownOptions()}
              selectedValue={selectedGroupId}
              onSelect={handleDropdownSelect}
              placeholder="Select Month"
              buttonVariant="primary"
              isLoading={!invoiceGroupsData}
              buttonClassName='rounded-full bg-gray-201 text-gray-700'
            />
            {isBillingAdmin && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={openCreateCycleDrawer}
                className="rounded-full h-9 w-9 shrink-0"
                aria-label="Create new billing cycle"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <CreateBillingCycleDrawer
        open={createCycleDrawerOpen}
        onOpenChange={setCreateCycleDrawerOpen}
        existingGroups={sortedInvoiceGroups}
        selectedMonth={createCycleMonth}
        selectedYear={createCycleYear}
        onMonthChange={setCreateCycleMonth}
        onYearChange={setCreateCycleYear}
        onCreate={handleCreateBillingCycle}
        isCreating={isCreatingInvoiceGroup}
      />

      {/* Content */}
      <div className="flex-1 overflow-auto relative">
        {!liveUpdatesAvailable && selectedGroupId && !isLoadingSelectedGroup && displayedInvoiceGroupDetails && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
            Live updates unavailable
          </p>
        )}
        <InvoiceTableRefactored
          searchValue={searchValue}
          selectedGroupId={selectedGroupId}
          invoiceGroupDetails={displayedInvoiceGroupDetails}
          isLoadingDetails={isLoadingInvoiceGroupDetails}
          isFetchingDetails={isFetchingInvoiceGroupDetails}
          refetchInvoiceGroups={refetchInvoiceGroupDetails}
          companies={companies}
          isBillingGenerationInProgress={isBillingGenerationInProgress || isGeneratingInvoices}
          onGenerationComplete={() => setIsGeneratingInvoices(false)}
          // onOpenPreviewModal={handleOpenPreviewModal} // TODO: Remove this if we don't need i
          onGenerateInvoices={handleGenerateInvoices}
          deepLinkInvoiceId={effectiveInvoiceId}
          onConsumeInvoiceDeepLink={consumeInvoiceDeepLink}
        />
      </div>
    </div>
  );
};

export default InvoicesTab;